import type { IOption } from './nestedSelectTypes';

/** Finds an option anywhere in the tree (branch nodes and leaves). Used when hydrating persisted ids. */
export function findOptionByValueInTree(
  roots: IOption[],
  value: string
): IOption | undefined {
  for (const node of roots) {
    if (node.value === value) return node;
    if (node.options?.length) {
      const found = findOptionByValueInTree(node.options, value);
      if (found) return found;
    }
  }
  return undefined;
}

export const flattenOptions = (
  rawOptions?: IOption[],
  removeOptions: boolean = true
): IOption[] => {
  if (rawOptions) {
    let options: IOption[] = [];
    const flattenMembers = rawOptions.map((m) => {
      if (m.options && m.options.length) {
        options = [...options, ...m.options];
      }
      return m;
    });

    return flattenMembers
      .concat(options.length ? flattenOptions(options, removeOptions) : options)
      .filter((item) => (removeOptions ? !item.options : true));
  } else return [];
};

/**
 * Mapa `id obszaru → id rodzica (w drzewie firmy)` dla węzłów z ustawionym `value`.
 * Węzeł syntetyczny bez `value` przekazuje `parentKey` tylko dzieciom.
 */
function buildAreaParentIdMap(
  roots: IOption[],
  parentKey: string | undefined = undefined
): Map<string, string | undefined> {
  const map = new Map<string, string | undefined>();

  for (const node of roots) {
    const selfKey =
      node.value !== undefined &&
        node.value !== null &&
        String(node.value).trim() !== ''
        ? String(node.value)
        : undefined;

    if (selfKey !== undefined) {
      map.set(selfKey, parentKey);
    }

    const childParent = selfKey ?? parentKey;

    if (node.options?.length) {
      const childMap = buildAreaParentIdMap(node.options, childParent);
      childMap.forEach((p, k) => map.set(k, p));
    }
  }

  return map;
}

function isNonEmptyAreaId(value: string | undefined | null): value is string {
  return (
    value !== undefined &&
    value !== null &&
    String(value).trim() !== ''
  );
}

/** Z zaznaczonych liści robi `area_ids`, doklejając rodzica gałęzi tylko gdy zaznaczone są wszystkie jej liście (Team, Function, TaskType, PriorityLevel). */
export function expandSelectedAreaIdsWithAncestors(
  areaTreeRoots: IOption[],
  selectedAreas: IOption[]
): string[] {
  const parentMap = buildAreaParentIdMap(areaTreeRoots);
  const selectedLeaves = flattenOptions(selectedAreas);
  const selectedIds = new Set<string>();

  for (const opt of selectedLeaves) {
    if (!isNonEmptyAreaId(opt.value)) continue;
    selectedIds.add(String(opt.value));
  }

  const out = new Set(selectedIds);
  const parentsToCheck = new Set<string>();

  for (const id of selectedIds) {
    const immediateParent = parentMap.get(id);
    if (isNonEmptyAreaId(immediateParent)) {
      parentsToCheck.add(String(immediateParent));
    }
  }

  for (const parentId of parentsToCheck) {
    const parentNode = findOptionByValueInTree(areaTreeRoots, parentId);
    if (!parentNode?.options?.length) continue;

    const leafIdsUnderParent = flattenOptions(parentNode.options)
      .map((leaf) => leaf.value)
      .filter(isNonEmptyAreaId)
      .map(String);

    if (
      leafIdsUnderParent.length > 0 &&
      leafIdsUnderParent.every((leafId) => selectedIds.has(leafId))
    ) {
      out.add(parentId);
    }
  }

  return Array.from(out);
}
